import { DEFAULT_ITEMS_COUNT_PER_REQUEST } from '@app/constants/limits';
import { fetchSalesReps } from '@app/services/accountRepository';
import { useDebouncedCallback } from '@app/utils/hooks';
import { isDefined } from '@app/utils/is';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import React from 'react';

const DEBOUNCE_TIME = 500;
const SALES_REP_SELECT_QUERY_KEY = 'sales-rep-select';

export const useSalesRepSelect = (
  defaultOption: Nullable<{
    id: number;
    name: string;
  }>,
) => {
  const queryClient = useQueryClient();
  const [search, setSearch] = React.useState('');

  const loadSalesReps = async ({ pageParam }: { pageParam: number }) => {
    return fetchSalesReps({ limit: DEFAULT_ITEMS_COUNT_PER_REQUEST, page: pageParam, search });
  };

  const { data, fetchNextPage, hasNextPage, isFetching } = useInfiniteQuery({
    gcTime: 0,
    getNextPageParam: (lastPage, pages) =>
      !isDefined(lastPage) || lastPage.data.length < DEFAULT_ITEMS_COUNT_PER_REQUEST ? undefined : pages.length + 1,
    initialPageParam: 1,
    queryFn: loadSalesReps,
    queryKey: [SALES_REP_SELECT_QUERY_KEY, { search }],
  });

  const options = React.useMemo(() => {
    if (!isDefined(data?.pages)) {
      return [];
    }

    const pages = data.pages.flatMap(page => page.data).map(page => ({ label: page.label, value: page.value.toString() }));
    if (!isDefined(defaultOption)) {
      return pages;
    }

    const hasDefaultOption = pages.find(page => page.value === defaultOption.id.toString());

    return hasDefaultOption ? pages : [...pages, { label: defaultOption.name, value: defaultOption.id.toString() }];
  }, [data, defaultOption]);

  const handleSearchChange = useDebouncedCallback((value: string) => {
    queryClient.removeQueries({ queryKey: [SALES_REP_SELECT_QUERY_KEY] });
    queryClient.setQueryData([SALES_REP_SELECT_QUERY_KEY, { search: value }], { pageParams: [], pages: [] });
    setSearch(value);
  }, DEBOUNCE_TIME);

  const handleLoadMore = React.useCallback(() => {
    if (hasNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage]);

  return {
    handleLoadMore,
    handleSearchChange,
    isLoading: isFetching,
    options,
  };
};
